import type { ChangePasswordRequest } from '@xphoto/shared';
import { apiFetch } from '../lib/http';

/**
 * Client typé des RÉGLAGES utilisateur (épopée Réglages/Admin).
 *
 * Comme les autres clients du front, l'erreur porte le STATUT HTTP et le code
 * d'erreur normalisé (`UNAUTHORIZED`…) : l'UI distingue ainsi « mot de passe
 * actuel incorrect » (401) des autres échecs. Les cookies de session sont
 * same-origin (`credentials: 'same-origin'`) — aucun jeton n'est manipulé en JS.
 */

/** Erreur d'appel API : statut HTTP + code d'erreur normalisé. */
export class SettingsApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`API ${status} ${code}`);
    this.name = 'SettingsApiError';
  }
}

async function errorCodeOf(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: { code?: string } };
    if (typeof body?.error?.code === 'string') return body.error.code;
  } catch {
    // Corps non-JSON (ou 204 sans corps) : code générique.
  }
  return 'UNKNOWN';
}

/**
 * `POST /api/auth/change-password` — change le mot de passe du compte actif.
 * 204 = succès (les autres sessions de l'utilisateur sont déconnectées côté
 * serveur). 401 = mot de passe actuel incorrect ; autre statut = échec générique.
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const payload: ChangePasswordRequest = { currentPassword, newPassword };
  const res = await apiFetch('/api/auth/change-password', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new SettingsApiError(res.status, await errorCodeOf(res));
}
